import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Circle, PhoneIncoming, PhoneMissed, PhoneOutgoing, Play, Plus, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { formatDate, formatDateTime, timeAgo } from '../../lib/format';
import { CustomerLink, DocLink } from '../../lib/links';
import type { Activity, LeadDetail } from '../../lib/leads';
import { DOC_NAME, STATUS_LABEL, inr, type DocSummary } from '../../lib/sales';
import { Badge, Button, ButtonLink, Skeleton, cx, inlineInputClass } from '../ui';

/**
 * The working tabs of a lead: everything about it in one place, each item linking to its details.
 * Counts come with the data so the tab bar can show "Calls (4)".
 */

export interface LeadCall {
  id: string; direction: 'outgoing' | 'incoming' | 'missed' | 'rejected'; startedAt: string; durationSec: number; note: string | null;
  recordingUrl: string | null; userName: string; aiStatus: string | null; aiSummary: string | null; aiSentiment: string | null; aiRating: number | null;
}
interface LeadTask {
  id: string; title: string; priority: 'low' | 'normal' | 'high'; status: 'todo' | 'in_progress' | 'done'; dueAt: string | null; assignedName: string; createdByName: string | null;
}

export function useLeadCalls(leadId: string) {
  return useQuery({ queryKey: ['lead-calls', leadId], queryFn: () => api.get<{ items: LeadCall[]; total: number }>(`/calls?lead=${leadId}&pageSize=100`) });
}
export function useLeadDocs(leadId: string) {
  return useQuery({ queryKey: ['documents', 'lead', leadId], queryFn: () => api.get<DocSummary[]>(`/documents?lead=${leadId}`) });
}
export function useLeadTasks(leadId: string) {
  return useQuery({ queryKey: ['tasks', 'lead', leadId], queryFn: () => api.get<LeadTask[]>(`/tasks?lead=${leadId}&status=all`) });
}

const NOTE_TYPES = new Set(['note', 'followup_done']);
export const noteCount = (items: Activity[]) => items.filter((a) => NOTE_TYPES.has(a.type) && a.body).length;

export function NotesTab({ lead }: { lead: LeadDetail }) {
  const notes = lead.activities.filter((a) => NOTE_TYPES.has(a.type) && a.body);
  if (!notes.length) return <p className="text-sm text-slate-500">No notes yet. Write what the customer said in the box above.</p>;
  return (
    <ol className="space-y-3">
      {notes.map((n, i) => (
        <li key={n.id} className="rounded-lg bg-slate-50 p-3 dark:bg-slate-800/60">
          <p className="text-sm whitespace-pre-wrap"><span className="mr-2 text-xs text-slate-400">{notes.length - i}.</span>{n.body}</p>
          <p className="mt-1 text-xs text-slate-500">{n.type === 'followup_done' ? 'Follow-up done · ' : ''}{n.userName ?? 'Someone'} · {formatDateTime(n.createdAt)}</p>
        </li>
      ))}
    </ol>
  );
}

const CALL_ICON = { outgoing: PhoneOutgoing, incoming: PhoneIncoming, missed: PhoneMissed, rejected: PhoneMissed };
const dur = (s: number) => (s >= 60 ? `${Math.floor(s / 60)}m ${s % 60}s` : `${s}s`);

export function CallsTab({ leadId }: { leadId: string }) {
  const calls = useLeadCalls(leadId);
  const [playing, setPlaying] = useState<string | null>(null);
  if (!calls.data) return <Skeleton className="h-24" />;
  if (!calls.data.items.length) return <p className="text-sm text-slate-500">No calls yet. Calls from the phone app show here by themselves.</p>;
  const talked = calls.data.items.reduce((s, c) => s + c.durationSec, 0);
  return (
    <div className="space-y-3">
      <p className="text-sm text-slate-500">{calls.data.total} call{calls.data.total === 1 ? '' : 's'} · {dur(talked)} talk time</p>
      <ol className="divide-y divide-slate-100 dark:divide-slate-800">
        {calls.data.items.map((c, i) => {
          const Icon = CALL_ICON[c.direction];
          const connected = c.durationSec > 0 && (c.direction === 'outgoing' || c.direction === 'incoming');
          return (
            <li key={c.id} className="py-2.5">
              <div className="flex items-center gap-3">
                <span className="w-6 text-right text-xs text-slate-400">{i + 1}</span>
                <Icon className={cx('size-4', connected ? 'text-emerald-600' : 'text-red-500')} />
                <div className="min-w-0 flex-1 text-sm">
                  <p><b className="font-medium">{c.direction === 'incoming' ? 'Incoming' : c.direction === 'outgoing' ? 'Outgoing' : 'Missed'}</b>{connected ? ` · ${dur(c.durationSec)}` : c.direction === 'outgoing' ? ' · not answered' : ''}</p>
                  <p className="text-xs text-slate-500">{c.userName} · {formatDateTime(c.startedAt)}</p>
                </div>
                {c.aiRating && <Badge tone="brand"><Sparkles className="mr-1 inline size-3" />{c.aiRating}/5</Badge>}
                {c.recordingUrl && <Button size="sm" variant="ghost" icon={<Play className="size-4" />} onClick={() => setPlaying(playing === c.id ? null : c.id)}>Listen</Button>}
              </div>
              {c.note && <p className="mt-1 ml-9 text-sm text-slate-600 dark:text-slate-400">{c.note}</p>}
              {c.aiSummary && <p className="mt-1 ml-9 rounded-lg bg-brand-50 p-2 text-sm dark:bg-brand-950/40">{c.aiSummary}</p>}
              {playing === c.id && c.recordingUrl && <audio className="mt-2 ml-9 w-[calc(100%-2.25rem)]" src={c.recordingUrl} controls autoPlay />}
            </li>
          );
        })}
      </ol>
    </div>
  );
}

export function QuotesTab({ lead }: { lead: LeadDetail }) {
  const { can } = useAuth();
  const docs = useLeadDocs(lead.id);
  const customer = useQuery({ queryKey: ['lead-customer', lead.id], queryFn: () => api.get<{ existing: { id: string; name: string } | null }>(`/leads/${lead.id}/customer`) });
  if (!docs.data) return <Skeleton className="h-24" />;
  return (
    <div className="space-y-3">
      {customer.data?.existing && <p className="text-sm">Customer: <CustomerLink id={customer.data.existing.id}>{customer.data.existing.name}</CustomerLink></p>}
      {docs.data.length === 0 ? <p className="text-sm text-slate-500">No quotations or invoices for this lead yet.</p> : (
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-slate-500 uppercase"><tr><th className="w-8 py-2">#</th><th>Document</th><th>Date</th><th>Status</th><th className="text-right">Amount</th></tr></thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
            {docs.data.map((d, i) => (
              <tr key={d.id}>
                <td className="py-2 text-slate-500">{i + 1}</td>
                <td><DocLink id={d.id}>{DOC_NAME[d.docType]} {d.number ?? '(draft)'}</DocLink></td>
                <td>{formatDate(d.issueDate)}</td>
                <td><Badge tone={STATUS_LABEL[d.status].tone}>{STATUS_LABEL[d.status].label}</Badge></td>
                <td className="text-right font-medium">{inr(d.grandTotal)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {can('documents.manage') && <ButtonLink to={`/sales/documents/new?type=quotation&forLead=${lead.id}`} variant="secondary" size="sm" icon={<Plus className="size-4" />}>Make a quotation</ButtonLink>}
    </div>
  );
}

export function TasksTab({ lead }: { lead: LeadDetail }) {
  const qc = useQueryClient();
  const tasks = useLeadTasks(lead.id);
  const [title, setTitle] = useState('');
  const [due, setDue] = useState('');
  const [busy, setBusy] = useState(false);
  const refresh = () => qc.invalidateQueries({ queryKey: ['tasks'] });
  const add = async () => {
    if (!title.trim()) return;
    setBusy(true);
    try {
      await api.post('/tasks', { title: title.trim(), leadId: lead.id, dueAt: due ? new Date(`${due}T18:00`).toISOString() : null, ...(lead.ownerId ? { assignedTo: lead.ownerId } : {}) });
      setTitle(''); setDue(''); await refresh();
    } catch (e) {
      // Only managers can give tasks to others: fall back to a task for yourself.
      try { await api.post('/tasks', { title: title.trim(), leadId: lead.id, dueAt: due ? new Date(`${due}T18:00`).toISOString() : null }); setTitle(''); setDue(''); await refresh(); }
      catch { toast.error(e instanceof Error ? e.message : 'Couldn’t add the task.'); }
    } finally { setBusy(false); }
  };
  const toggle = async (t: LeadTask) => {
    try { await api.patch(`/tasks/${t.id}`, { status: t.status === 'done' ? 'todo' : 'done' }); await refresh(); }
    catch (e) { toast.error(e instanceof Error ? e.message : 'Couldn’t update the task.'); }
  };
  return (
    <div className="space-y-3">
      <form className="flex flex-wrap gap-2" onSubmit={(e) => { e.preventDefault(); void add(); }}>
        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Send brochure, arrange site visit…" className={cx(inlineInputClass, 'min-w-0 flex-1')} />
        <input type="date" value={due} onChange={(e) => setDue(e.target.value)} aria-label="Due date" className={inlineInputClass} />
        <Button type="submit" size="sm" loading={busy} disabled={!title.trim()}>Add task</Button>
      </form>
      {!tasks.data ? <Skeleton className="h-16" /> : tasks.data.length === 0 ? <p className="text-sm text-slate-500">No tasks for this lead.</p> : (
        <ol className="divide-y divide-slate-100 dark:divide-slate-800">
          {tasks.data.map((t, i) => (
            <li key={t.id} className="flex items-center gap-3 py-2.5">
              <span className="w-6 text-right text-xs text-slate-400">{i + 1}</span>
              <button onClick={() => void toggle(t)} aria-label={t.status === 'done' ? 'Mark not done' : 'Mark done'}>
                {t.status === 'done' ? <CheckCircle2 className="size-5 text-emerald-600" /> : <Circle className="size-5 text-slate-400" />}
              </button>
              <div className="min-w-0 flex-1 text-sm">
                <p className={cx('font-medium', t.status === 'done' && 'text-slate-400 line-through')}>{t.title}</p>
                <p className="text-xs text-slate-500">{t.assignedName}{t.dueAt ? ` · due ${formatDate(t.dueAt)}` : ''}{t.status !== 'done' && t.dueAt && new Date(t.dueAt) < new Date() ? ' (late)' : ''}</p>
              </div>
              {t.priority === 'high' && <Badge tone="red">Urgent</Badge>}
            </li>
          ))}
        </ol>
      )}
      <p className="text-xs text-slate-500">Last change {timeAgo(lead.lastActivityAt)}</p>
    </div>
  );
}
